/**
 * YUO · Personalization — "Living light field" edition (Feb 2026).
 *
 * A single dark chamber with radial washes. Ten body forms are rendered as
 * pure light fields (three glowing bars) that flip on tap to reveal a
 * character description. No borders, no cards, no grids of dark rectangles.
 *
 * Flow (preserved from previous impl so the API contract still works):
 *   0. Intro
 *   1. Body — pick 1 of 10 (this screen)
 *   2..N. Diagnostic blocks (Activity, Positivity, Soma, Thinking, IQ)
 *   Result — 3D stone + type + YUO analysis
 *
 * Everything must open — including the locked (Free plan) invitation.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TypeStone from "@/components/TypeStone";

// ---------------------------------------------------------------------------
// Static form data — exact spec (numerology sequence, fixed colours & shapes)
// ---------------------------------------------------------------------------
const FORMS = [
  { id: 1,  c: "213,217,227", w: [74, 74, 74], ru: ["Прямоугольник", "Линия"],       en: ["Rectangle", "Line"],       tru: "Ровная вертикаль: плечи, пояс и бёдра держат одну линию. Тело не спорит с собой, поэтому ход спокойный и экономный. Такой человек редко торопится с ответом и почти никогда не повышает голос — он держит форму там, где другие расходуются.", ten: "A steady vertical: shoulders, waist and hips hold one line. The body doesn't argue with itself, so the movement is calm and economical. This person rarely rushes an answer and almost never raises their voice — they hold shape where others burn out." },
  { id: 2,  c: "255,122,102", w: [58, 70, 86], ru: ["Груша", "Поток"],                en: ["Pear", "Flow"],            tru: "Мягкие округлые линии, вес собран внизу — тело стоит на земле уверенно. Открытый взгляд, тёплая мимика, потребность в тактильном контакте: близость для него разговор, а не награда. Решения приходят через ощущение, а не через расчёт.", ten: "Soft round lines, weight gathered low — the body stands confidently on the earth. Open gaze, warm mimicry, need for tactile contact: closeness is conversation, not reward. Decisions come through feeling, not calculation." },
  { id: 3,  c: "245,213,71",  w: [84, 48, 84], ru: ["Песочные часы", "Гармония"],     en: ["Hourglass", "Harmony"],    tru: "Верх и низ уравновешены, центр собран — тело само по себе пропорция. Такой человек чувствует меру в чужих словах и быстро находит общий ритм в группе. Его сила не в напоре, а в том, что рядом с ним перестаёт качать.", ten: "Top and bottom balanced, centre gathered — the body is proportion itself. This person senses measure in others' words and quickly finds a common rhythm. Their power is not force but the way rocking calms around them." },
  { id: 4,  c: "168,85,247",  w: [90, 68, 46], ru: ["Обратный треугольник", "Грация"], en: ["Inverse Triangle", "Grace"], tru: "Широкий разворот плеч, узкая опора — вес вынесен вверх, и тело живёт на вдохе. Отсюда быстрый старт, лёгкость на подъём и склонность браться первым. Внимание идёт вперёд, к горизонту, а не вниз, к деталям.", ten: "Wide shoulder span, narrow base — weight carried high, the body lives on the in-breath. Fast start, easy lift, tendency to take on first. Attention moves forward to the horizon, not down to details." },
  { id: 5,  c: "127,181,230", w: [66, 88, 62], ru: ["Яблоко", "Астеник"],             en: ["Apple", "Asthenic"],       tru: "Центр наполнен, конечности лёгкие — вся жизнь собрана в середине корпуса. Тонкая кожа, быстрая реакция на холод, шум и чужое настроение. Такой человек первым замечает сдвиг в комнате и первым устаёт от плотного контакта.", ten: "Full centre, light limbs — all life gathered in the middle of the torso. Thin skin, fast reaction to cold, noise and others' moods. This person is first to notice a shift in the room and first to tire from dense contact." },
  { id: 6,  c: "52,255,155",  w: [76, 86, 76], ru: ["Овал", "Нормостеник"],           en: ["Oval", "Normostenic"],     tru: "Округлая, замкнутая на себя форма без резких переходов — тело держит ровный тонус. Восстановление быстрое, выносливость на длинной дистанции выше, чем на короткой. Такой человек не выигрывает рывком, он остаётся в деле, когда остальные вышли.", ten: "Rounded, self-contained form with no sharp transitions — the body holds even tone. Recovery is fast, endurance over long distance higher than short. This person doesn't win by burst; they stay in when others leave." },
  { id: 7,  c: "245,166,35",  w: [54, 90, 54], ru: ["Ромб", "Вектор"],                en: ["Rhombus", "Vector"],       tru: "Узкие концы, плотная середина: тело собрано в одну точку и оттуда бьёт. Такой человек направлен — он выбирает одну задачу и вкладывает в неё всё. Пока цель есть, силы кажутся бесконечными; когда цели нет, накрывает пустотой.", ten: "Narrow ends, dense middle: the body gathers into one point and strikes from there. This person is directed — they pick one task and pour everything in. While the goal exists, strength feels infinite; when it's gone, emptiness lands." },
  { id: 8,  c: "239,68,68",   w: [92, 74, 58], ru: ["Трапеция", "Монолит"],           en: ["Trapezoid", "Monolith"],   tru: "Плотный каркас, широкий верх, ясная линия к опоре — тело как одно целое, без люфта. Такой человек выдерживает нагрузку, которую другие делят, и почти не показывает усилия. Его слово тяжёлое: он говорит редко и потом не отступает.", ten: "Dense frame, broad top, clear line to base — the body is one whole with no slack. This person bears loads others split and barely shows effort. Their word is heavy: they speak rarely and don't back down." },
  { id: 9,  c: "134,239,172", w: [70, 94, 70], ru: ["Сфера", "Земля"],                en: ["Sphere", "Earth"],         tru: "Мягкий объём по всему контуру, низкий центр тяжести — тело трудно сдвинуть и легко к нему прислониться. Такой человек создаёт вокруг себя место, где другим спокойно, и часто держит группу без всякой роли.", ten: "Soft volume all around, low centre of gravity — hard to move, easy to lean on. This person creates a place around themselves where others feel calm, and often holds a group without any assigned role." },
  { id: 10, c: "176,107,255", w: [46, 46, 46], ru: ["Колонна", "Ось"],                en: ["Column", "Axis"],          tru: "Узкая вытянутая вертикаль, всё лишнее снято — тело как ось, вокруг которой идёт движение. Такой человек тянется вверх и живёт мыслью раньше, чем телом; он видит структуру там, где остальные видят поток.", ten: "Narrow elongated vertical, everything extra stripped — the body is an axis with movement around it. This person reaches upward and lives in thought before body; they see structure where others see flow." },
];

const GREEN = "52,255,150";
const INK = "var(--yuo-ink)";
const FONT = '"JetBrains Mono", ui-monospace, monospace';

// ---------------------------------------------------------------------------
// Shared little atoms
// ---------------------------------------------------------------------------
const Stone = ({ rgb, size = 14, breathe = false }) => (
  <span style={{
    display: "inline-block",
    width: size, height: size, borderRadius: "50%",
    background: `radial-gradient(34% 30% at 32% 26%, #ffffff, rgb(${rgb}) 22%, rgba(${rgb},0.35) 62%, #01040a 100%)`,
    boxShadow: `0 0 10px rgba(${rgb},0.8)`,
    animation: breathe ? "yuo-breathe 3.2s ease-in-out infinite" : undefined,
    flex: "0 0 auto",
  }} />
);

const LangPill = ({ active, onClick, children, testId }) => (
  <span
    onClick={onClick}
    data-testid={testId}
    style={{
      padding: "7px 18px", borderRadius: 999, cursor: "pointer", userSelect: "none",
      fontSize: 10, letterSpacing: "0.22em", fontFamily: FONT,
      color: active ? INK : "var(--fg-38)",
      background: active
        ? `radial-gradient(120% 160% at 30% 0%, rgba(${GREEN},0.34), rgba(${GREEN},0.10))`
        : "var(--fg-05)",
      boxShadow: active ? `0 0 26px rgba(${GREEN},0.32)` : "none",
      transition: "background .6s, box-shadow .6s",
    }}>
    {children}
  </span>
);

const NextCapsule = ({ active, disabled, onClick, testId, children }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    style={{
      display: "inline-flex", alignItems: "center", gap: 14,
      padding: "15px 28px", borderRadius: 999, border: "none",
      fontFamily: FONT, fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase",
      color: active ? INK : "var(--fg-30)",
      background: active
        ? `radial-gradient(120% 160% at 30% 0%, rgba(${GREEN},0.34), rgba(${GREEN},0.10))`
        : "var(--fg-04)",
      boxShadow: active ? `0 0 26px rgba(${GREEN},0.32)` : "none",
      opacity: disabled ? 0.55 : 1,
      cursor: disabled ? "default" : "pointer",
      transition: ".6s",
    }}>
    <Stone rgb={GREEN} size={18} />
    {children}
  </button>
);

const BackStone = ({ onClick, label }) => (
  <div onClick={onClick} data-testid="upgrade-back"
    style={{
      display: "inline-flex", alignItems: "center", gap: 12,
      fontSize: 11, letterSpacing: "0.2em", fontFamily: FONT,
      color: "var(--fg-4)", cursor: "pointer", userSelect: "none",
    }}>
    <Stone rgb={GREEN} size={14} />
    <span>{label}</span>
  </div>
);

const Progress = ({ pct, current, total }) => (
  <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 20, marginTop: 34 }}>
    <div style={{ position: "relative", flex: 1, height: 2, borderRadius: 999, background: "var(--fg-06)" }}>
      <div style={{
        position: "absolute", inset: "0 auto 0 0",
        width: `${pct}%`, borderRadius: 999,
        background: `linear-gradient(90deg, rgba(79,157,255,0.9), rgba(${GREEN},0.9))`,
        boxShadow: `0 0 18px rgba(${GREEN},0.5)`,
        transition: "width 800ms cubic-bezier(.2,.7,.2,1)",
      }} />
    </div>
    <span style={{ fontSize: 10.5, letterSpacing: "0.22em", color: "var(--fg-32)" }}>
      {String(current).padStart(2, "0")} / {String(total).padStart(2, "0")}
    </span>
  </div>
);

// ---------------------------------------------------------------------------
// Chamber shell — a dark chamber with drifting radial ambience
// ---------------------------------------------------------------------------
const Chamber = ({ children }) => (
  <div style={{
    position: "relative", overflow: "clip",
    minHeight: "100vh", padding: "44px 54px 90px",
    background: "var(--yuo-bg)", color: INK, fontFamily: FONT,
  }}>
    <div aria-hidden style={{
      position: "absolute", inset: "2%", pointerEvents: "none",
      background:
        `radial-gradient(38% 26% at 24% 12%, rgba(${GREEN},0.07), transparent 78%),` +
        "radial-gradient(34% 24% at 82% 34%, rgba(176,107,255,0.06), transparent 76%)," +
        "radial-gradient(40% 30% at 50% 92%, rgba(79,157,255,0.05), transparent 80%)",
      animation: "yuo-drift 38s ease-in-out infinite",
    }} />
    {children}
  </div>
);

// ---------------------------------------------------------------------------
// Body form field — three glowing bars that flip to reveal the character
// ---------------------------------------------------------------------------
function FormField({ form, index, open, chosen, onFlip, onChoose, lang, tThis }) {
  const glow = open || chosen ? 0.16 : 0.07;
  const barGlow = open ? 34 : 24;
  const face = {
    position: "absolute", inset: 0, display: "flex", flexDirection: "column",
    padding: "26px 26px 28px", borderRadius: 34, boxSizing: "border-box",
    backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden",
    background: `radial-gradient(120% 100% at 26% 0%, rgba(${form.c},${glow}), rgba(${form.c},0.03) 58%, transparent 100%)`,
    boxShadow: chosen ? `0 0 60px rgba(${form.c},0.22)` : "none",
    transition: "background .8s ease, box-shadow .8s ease",
  };
  const id2 = String(form.id).padStart(2, "0");
  const [name, key] = lang === "ru" ? form.ru : form.en;
  const description = lang === "ru" ? form.tru : form.ten;

  return (
    <div
      onClick={onFlip}
      data-testid={`upgrade-body-card-${form.id}`}
      style={{
        perspective: 1400, cursor: "pointer", height: 392,
        animation: `yuo-emerge .9s cubic-bezier(.2,.7,.2,1) both`,
        animationDelay: `${Math.min(index * 0.05, 0.6)}s`,
      }}>
      <div style={{
        position: "relative", width: "100%", height: "100%",
        transformStyle: "preserve-3d",
        transform: open ? "rotateY(180deg)" : "none",
        transition: "transform 1s cubic-bezier(.2,.7,.2,1)",
      }}>
        {/* FRONT — figure */}
        <div style={face}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
            <span style={{ fontSize: 12, letterSpacing: "0.24em", color: `rgba(${form.c},0.9)` }}>{id2}</span>
            <span style={{ fontSize: 10, letterSpacing: "0.2em", color: "var(--fg-22)" }}>{id2} / 10</span>
          </div>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 11 }}>
            {form.w.map((pct, i) => (
              <div key={i} style={{
                width: `${pct}%`, height: 34, borderRadius: 999,
                background: `radial-gradient(60% 120% at 34% 20%, rgba(${form.c},0.98), rgba(${form.c},0.72) 62%, rgba(${form.c},0.5) 100%)`,
                boxShadow: `0 0 ${barGlow}px rgba(${form.c},0.45), inset -4px -6px 12px rgba(0,0,0,0.35)`,
                transition: "box-shadow .7s, width .7s cubic-bezier(.2,.7,.2,1)",
              }} />
            ))}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            <span style={{ fontSize: 14, letterSpacing: "0.2em", color: "var(--fg-88)" }}>{name.toUpperCase()}</span>
            <span style={{ fontSize: 10.5, letterSpacing: "0.24em", textTransform: "uppercase", color: `rgba(${form.c},0.7)` }}>{key}</span>
          </div>
        </div>
        {/* BACK — description + "this is me" capsule */}
        <div style={{ ...face, transform: "rotateY(180deg)", gap: 20 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
            <span style={{ fontSize: 12, letterSpacing: "0.24em", color: `rgba(${form.c},0.9)` }}>{id2}</span>
            <span style={{ fontSize: 10.5, letterSpacing: "0.24em", textTransform: "uppercase", color: `rgba(${form.c},0.7)` }}>{name.toUpperCase()}</span>
          </div>
          <p style={{ margin: 0, flex: 1, fontSize: 12.5, lineHeight: 1.95, letterSpacing: "0.03em", color: "var(--fg-66)" }}>
            {description}
          </p>
          <div
            onClick={(e) => { e.stopPropagation(); onChoose(); }}
            data-testid={`upgrade-body-choose-${form.id}`}
            style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
              padding: "14px 22px", borderRadius: 999, cursor: "pointer", userSelect: "none",
              fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase",
              color: `rgba(${form.c},0.95)`,
              background: `radial-gradient(120% 160% at 20% 0%, rgba(${form.c},${chosen ? 0.34 : 0.20}), rgba(${form.c},0.05))`,
              boxShadow: `0 0 ${chosen ? 38 : 26}px rgba(${form.c},${chosen ? 0.40 : 0.24})`,
              transition: "background .6s, box-shadow .6s",
            }}>
            <Stone rgb={form.c} size={14} breathe={chosen} />
            <span>{chosen ? tThis.chosen : tThis.choose}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function Personalization() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const [lang] = useState("en");
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(0);
  const [bodyId, setBodyId] = useState(null);
  const [openForm, setOpenForm] = useState(null);
  const [answers, setAnswers] = useState({});
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.upgradeConfig()
      .then((c) => {
        setConfig(c);
        if (c.profile && c.profile.type_id) setResult(c.profile);
      })
      .catch((e) => setError(e?.message || "Failed to load"));
  }, []);

  const t = useMemo(() => ({
    en: {
      back: "back",
      body_title: "First — the body", body_hint: "Tap a field to reveal it. Pick the form that resembles you.",
      choose: "this is me", chosen: "chosen",
      next: "next", next_hint_choose: "pick one form to continue", next_hint_ready: "form accepted · next — breath",
      intro_kicker: "Conscious Upgrade · Growth",
      intro_title: "Personalization",
      intro_body: "YUO Personalization reads the shape of your body, the vector of your action, the filter of your perception and your current level of consciousness — and returns one whole portrait.",
      intro_body2: "Nothing is fixed. This is a mirror of who you are today, and a map of who you can become.",
      begin: "begin",
      analysing: "YUO is reading your pattern…",
      submit: "reveal my type",
      submit_hint: "answer all to reveal the type",
      answered_hint: "type is being read · continue",
      profile_kicker: "Your YUO type",
      iq: "current IQ", thinking: "thinking", gland: "dominant gland", vectors: "vectors",
      strengths: "strengths", shadows: "shadows at this IQ", practices: "practices to climb", arc: "growth arc",
      redo: "retake", done: "enter YUO",
      locked_kicker: "Personalization · Pro & Master",
      locked_title: "The full portrait lives here",
      locked_body: "The 10-form diagnostic and your personal YUO portrait are part of Pro & Master. Upgrade to open the journey — you can retake it any time.",
      upgrade: "upgrade to Pro",
    },
    ru: {
      back: "назад",
      body_title: "Сначала — тело", body_hint: "Коснитесь карточки — она раскроется. Выберите ту форму, которая похожа на вас.",
      choose: "это я", chosen: "выбрано",
      next: "дальше", next_hint_choose: "выберите одну форму, чтобы продолжить", next_hint_ready: "форма принята · шаг — дыхание",
      intro_kicker: "Осознанный Upgrade · Рост",
      intro_title: "Персонализация",
      intro_body: "YUO читает форму твоего тела, вектор действия, фильтр восприятия и текущий уровень сознания — и возвращает один целостный портрет.",
      intro_body2: "Ничего не зафиксировано. Это зеркало того, кто ты сегодня, и карта того, кем ты можешь стать.",
      begin: "начать",
      analysing: "YUO читает твой рисунок…",
      submit: "открыть мой тип",
      submit_hint: "ответьте на все вопросы, чтобы открыть тип",
      answered_hint: "тип читается · дальше",
      profile_kicker: "Твой YUO-тип",
      iq: "текущий IQ", thinking: "мышление", gland: "ведущая железа", vectors: "векторы",
      strengths: "сильные стороны", shadows: "тени этого уровня", practices: "практики роста", arc: "путь роста",
      redo: "пройти заново", done: "войти в YUO",
      locked_kicker: "Персонализация · Pro и Master",
      locked_title: "Полный портрет живёт здесь",
      locked_body: "Диагностика 10 форм и персональный YUO-портрет входят в планы Pro и Master. Улучши план, чтобы открыть путь — пересдать можно в любой момент.",
      upgrade: "улучшить до Pro",
    },
  })[lang], [lang]);

  const header = (
    <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 24 }}>
      <BackStone onClick={() => navigate(-1)} label={t.back} />
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <LangPill active testId="upgrade-lang-en">EN</LangPill>
      </div>
    </div>
  );

  // ---- loading ---------------------------------------------------------------
  if (!config) {
    return (
      <Chamber>
        <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center",
          color: "var(--fg-4)", letterSpacing: "0.28em", textTransform: "uppercase", fontSize: 11 }}>
          {lang === "ru" ? "загружаем…" : "loading…"}
        </div>
      </Chamber>
    );
  }

  // ---- locked (Free plan) ----------------------------------------------------
  if (config.locked) {
    return (
      <Chamber>
        {header}
        <div style={{ maxWidth: 620, margin: "80px auto 0", textAlign: "center",
          animation: "yuo-emerge .9s cubic-bezier(.2,.7,.2,1) both" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}>
            <TypeStone color="#B06BFF" size={160} />
          </div>
          <div data-testid="upgrade-locked" style={{ color: "var(--lit-purple-16)", fontSize: 10.5, letterSpacing: "0.28em",
            textTransform: "uppercase", marginBottom: 12 }}>{t.locked_kicker}</div>
          <div style={{ fontSize: 34, fontWeight: 300, letterSpacing: "0.02em", marginBottom: 18 }}>
            {t.locked_title}
          </div>
          <p style={{ margin: "0 auto 32px", maxWidth: 520, fontSize: 13, lineHeight: 1.85,
            color: "var(--fg-5)" }}>
            {t.locked_body}
          </p>
          <NextCapsule active onClick={() => navigate("/paywall?plan=pro&feature=personalization")}
            testId="upgrade-cta">
            {t.upgrade}
          </NextCapsule>
        </div>
      </Chamber>
    );
  }

  const blocks = config.blocks || [];
  const TOTAL_STEPS = 2 + blocks.length; // intro + body + N blocks
  const progressPct = Math.min(100, ((step + 1) / TOTAL_STEPS) * 100);

  const goto = (n) => { setError(null); setStep(n); window.scrollTo({ top: 0 }); };

  const submitAll = async () => {
    setSubmitting(true); setError(null);
    try {
      const r = await api.upgradeSubmit({ body_form_id: bodyId, answers, lang });
      setResult(r);
      refreshUser?.();
    } catch (e) {
      const msg = e?.detail?.[lang === "ru" ? "message_ru" : "message_en"] || e?.message || "Error";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ---- result ---------------------------------------------------------------
  if (result) {
    const typeName = lang === "ru" ? result.type_name_ru : result.type_name_en;
    const thinking = lang === "ru" ? result.thinking_ru : result.thinking_en;
    const gland = lang === "ru" ? result.gland_ru : result.gland_en;
    const vectors = lang === "ru" ? result.vectors_ru : result.vectors_en;
    const iqDesc = lang === "ru" ? result.iq_desc_ru : result.iq_desc_en;
    const iqBadge = { standard: lang === "ru" ? "Стандартный" : "Standard",
      individual: lang === "ru" ? "Индивидуальный" : "Individual",
      genius: lang === "ru" ? "Гениальный" : "Genius" }[result.iq_level];
    const color = result.body_color || "#B06BFF";
    const rgb = hexToRgb(color);
    const list = (label, items) => items?.length ? (
      <div data-testid={`upgrade-list-${label}`} style={{
        padding: "22px 24px", borderRadius: 28,
        background: `radial-gradient(120% 100% at 26% 0%, rgba(${rgb},0.14), rgba(${rgb},0.03) 58%, transparent 100%)`,
      }}>
        <div style={{ color: `rgba(${rgb},0.8)`, fontSize: 10, letterSpacing: "0.28em", textTransform: "uppercase", marginBottom: 12 }}>
          {label}
        </div>
        <ul style={{ margin: 0, paddingLeft: 18, color: "var(--fg-78)", fontSize: 13, lineHeight: 1.85 }}>
          {items.map((s, i) => <li key={i} style={{ marginBottom: 4 }}>{s}</li>)}
        </ul>
      </div>
    ) : null;

    return (
      <Chamber>
        {header}
        <div style={{ maxWidth: 860, margin: "0 auto", paddingTop: 30,
          animation: "yuo-emerge 1s cubic-bezier(.2,.7,.2,1) both" }}>
          <div style={{ textAlign: "center", marginBottom: 46 }}>
            <div style={{ color: `rgba(${rgb},0.8)`, fontSize: 10, letterSpacing: "0.28em", textTransform: "uppercase", marginBottom: 18 }}>
              {t.profile_kicker}
            </div>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <TypeStone color={color} size={260} />
            </div>
            <div style={{ fontSize: 34, fontWeight: 300, letterSpacing: "0.02em", marginTop: 20, color }}>
              {String(result.type_id).padStart(2, "0")} · {typeName}
            </div>
            <div style={{ color: "var(--fg-6)", fontSize: 13, marginTop: 8 }}>{thinking}</div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginTop: 20,
              padding: "8px 18px", borderRadius: 999,
              background: `radial-gradient(120% 160% at 20% 0%, rgba(${rgb},0.24), rgba(${rgb},0.05))`,
              boxShadow: `0 0 22px rgba(${rgb},0.28)`,
              color: `rgba(${rgb},0.95)`, fontSize: 10.5, letterSpacing: "0.22em", textTransform: "uppercase" }}
              data-testid="upgrade-iq-badge">
              <Stone rgb={rgb} size={12} />
              {t.iq} · {iqBadge}
            </div>
          </div>

          {result.summary && (
            <div data-testid="upgrade-summary" style={{
              padding: "26px 28px", borderRadius: 30, marginBottom: 22,
              background: `radial-gradient(120% 100% at 26% 0%, rgba(${rgb},0.14), rgba(${rgb},0.03) 58%, transparent 100%)`,
              boxShadow: `0 0 60px rgba(${rgb},0.14) inset`,
              fontSize: 15, lineHeight: 1.75, color: "var(--fg-82)",
              whiteSpace: "pre-wrap",
            }}>
              {result.summary}
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 18 }}>
            {list(t.strengths, result.strengths)}
            {list(t.shadows, result.shadows)}
            {list(t.practices, result.practices)}
          </div>

          {result.growth_arc && (
            <div data-testid="upgrade-growth-arc" style={{
              marginTop: 22, padding: "24px 26px", borderRadius: 28,
              background: `radial-gradient(120% 100% at 26% 0%, rgba(${rgb},0.18), rgba(${rgb},0.03) 58%, transparent 100%)`,
              boxShadow: `0 0 44px rgba(${rgb},0.14) inset`,
            }}>
              <div style={{ color: `rgba(${rgb},0.85)`, fontSize: 10, letterSpacing: "0.28em", textTransform: "uppercase", marginBottom: 12 }}>
                {t.arc}
              </div>
              <div style={{ fontSize: 14.5, lineHeight: 1.8, color: "var(--fg-82)" }}>
                {result.growth_arc}
              </div>
            </div>
          )}

          <div style={{ marginTop: 22, padding: "22px 26px", borderRadius: 28,
            background: `radial-gradient(120% 100% at 26% 0%, rgba(${rgb},0.10), rgba(${rgb},0.02) 58%, transparent 100%)` }}>
            <MetaRow label={t.thinking} value={thinking} rgb={rgb} />
            <MetaRow label={t.gland} value={gland} rgb={rgb} />
            <MetaRow label={t.vectors} value={vectors} rgb={rgb} />
            <MetaRow label={t.iq} value={`${iqBadge} — ${iqDesc}`} rgb={rgb} last />
          </div>

          <div style={{ display: "flex", gap: 14, marginTop: 34, justifyContent: "center", flexWrap: "wrap" }}>
            <button
              onClick={() => { setResult(null); setBodyId(null); setAnswers({}); setStep(0); }}
              data-testid="upgrade-redo"
              style={{
                padding: "13px 26px", borderRadius: 999, border: "none",
                background: "var(--fg-05)", color: "var(--fg-7)",
                fontFamily: FONT, fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase",
                cursor: "pointer",
              }}>
              {t.redo}
            </button>
            <NextCapsule active onClick={() => navigate("/mirror")} testId="upgrade-done">
              {t.done}
            </NextCapsule>
          </div>
        </div>
      </Chamber>
    );
  }

  // ---- shell for every non-terminal step ------------------------------------
  const wrapper = (children) => (
    <Chamber>
      {header}
      <Progress pct={progressPct} current={step + 1} total={TOTAL_STEPS} />
      <div style={{ position: "relative", marginTop: 44 }}>
        {children}
        {error && (
          <div data-testid="upgrade-error" style={{
            marginTop: 22, padding: "12px 18px", borderRadius: 999,
            background: "rgba(239,68,68,0.10)", color: "var(--lit-coral-9)",
            fontSize: 12, letterSpacing: "0.14em",
          }}>{error}</div>
        )}
      </div>
    </Chamber>
  );

  // ---- Step 0 · Intro -------------------------------------------------------
  if (step === 0) {
    return wrapper(
      <div data-testid="upgrade-intro" style={{ maxWidth: 760,
        animation: "yuo-emerge .9s cubic-bezier(.2,.7,.2,1) both" }}>
        <div style={{ color: `rgba(${GREEN},0.75)`, fontSize: 10, letterSpacing: "0.28em",
          textTransform: "uppercase", marginBottom: 14 }}>{t.intro_kicker}</div>
        <h1 style={{ margin: 0, fontSize: 40, fontWeight: 300, letterSpacing: "0.04em", color: INK }}>
          {t.intro_title}
        </h1>
        <p style={{ marginTop: 18, fontSize: 13, lineHeight: 1.85, color: "var(--fg-55)", maxWidth: 640 }}>
          {t.intro_body}
        </p>
        <p style={{ marginTop: 8, fontSize: 12.5, lineHeight: 1.85, color: "var(--fg-35)", maxWidth: 640 }}>
          {t.intro_body2}
        </p>
        <div style={{ marginTop: 40 }}>
          <NextCapsule active onClick={() => goto(1)} testId="upgrade-begin">
            {t.begin}
          </NextCapsule>
        </div>
      </div>,
    );
  }

  // ---- Step 1 · Body form pick ---------------------------------------------
  if (step === 1) {
    return wrapper(
      <div data-testid="upgrade-body-step">
        <div style={{ maxWidth: 760, marginBottom: 44,
          animation: "yuo-emerge .9s cubic-bezier(.2,.7,.2,1) both" }}>
          <h1 style={{ margin: 0, fontSize: 40, fontWeight: 300, letterSpacing: "0.04em", color: INK }}>
            {t.body_title}
          </h1>
          <p style={{ margin: "14px 0 0", fontSize: 13, lineHeight: 1.8, color: "var(--fg-42)" }}>
            {t.body_hint}
          </p>
        </div>

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(268px, 1fr))",
          gap: 30,
        }}>
          {FORMS.map((form, i) => (
            <FormField
              key={form.id}
              form={form}
              index={i}
              open={openForm === form.id}
              chosen={bodyId === form.id}
              onFlip={() => setOpenForm(openForm === form.id ? null : form.id)}
              onChoose={() => setBodyId(bodyId === form.id ? null : form.id)}
              lang={lang}
              tThis={{ choose: t.choose, chosen: t.chosen }}
            />
          ))}
        </div>

        <div style={{ marginTop: 58, display: "flex", alignItems: "center", gap: 22, flexWrap: "wrap" }}>
          <NextCapsule
            active={!!bodyId}
            disabled={!bodyId}
            onClick={() => bodyId && goto(2)}
            testId="upgrade-body-continue">
            {t.next}
          </NextCapsule>
          <span style={{ fontSize: 10.5, letterSpacing: "0.16em", color: "var(--fg-28)" }}>
            {bodyId ? t.next_hint_ready : t.next_hint_choose}
          </span>
        </div>
      </div>,
    );
  }

  // ---- Steps 2..N · diagnostic blocks --------------------------------------
  const blockIdx = step - 2;
  const currentBlock = blocks[blockIdx];
  if (currentBlock) {
    const setAnswer = (qid, oid) => setAnswers((a) => ({ ...a, [qid]: oid }));
    const allAnswered = currentBlock.questions.every((q) => answers[q.id]);
    const isLast = blockIdx === blocks.length - 1;
    return wrapper(
      <div data-testid={`upgrade-block-${currentBlock.id}`}
        style={{ animation: "yuo-emerge .9s cubic-bezier(.2,.7,.2,1) both" }}>
        <div style={{ maxWidth: 760, marginBottom: 40 }}>
          <div style={{ color: `rgba(${GREEN},0.75)`, fontSize: 10, letterSpacing: "0.28em", textTransform: "uppercase", marginBottom: 12 }}>
            {lang === "ru" ? currentBlock.subtitle_ru : currentBlock.subtitle_en}
          </div>
          <h1 style={{ margin: 0, fontSize: 40, fontWeight: 300, letterSpacing: "0.04em", color: INK }}>
            {lang === "ru" ? currentBlock.title_ru : currentBlock.title_en}
          </h1>
        </div>

        {currentBlock.questions.map((q, qi) => (
          <div key={q.id} data-testid={`upgrade-q-${q.id}`} style={{ marginBottom: 40 }}>
            <div style={{ color: "var(--fg-32)", fontSize: 10.5, letterSpacing: "0.24em", marginBottom: 12 }}>
              {String(qi + 1).padStart(2, "0")} / {String(currentBlock.questions.length).padStart(2, "0")}
            </div>
            <div style={{ fontSize: 17, lineHeight: 1.6, marginBottom: 18, color: "var(--fg-88)", fontWeight: 300, letterSpacing: "0.02em" }}>
              {lang === "ru" ? q.prompt_ru : q.prompt_en}
            </div>
            <div style={{ display: "grid", gap: 12 }}>
              {q.options.map((o) => {
                const active = answers[q.id] === o.id;
                return (
                  <div
                    key={o.id}
                    onClick={() => setAnswer(q.id, o.id)}
                    data-testid={`upgrade-opt-${q.id}-${o.id}`}
                    style={{
                      textAlign: "left", padding: "16px 22px", borderRadius: 22,
                      cursor: "pointer",
                      color: active ? INK : "var(--fg-72)",
                      fontFamily: FONT, fontSize: 13.5, lineHeight: 1.6,
                      background: active
                        ? `radial-gradient(120% 160% at 20% 0%, rgba(${GREEN},0.20), rgba(${GREEN},0.04))`
                        : "var(--fg-04)",
                      boxShadow: active ? `0 0 28px rgba(${GREEN},0.22)` : "none",
                      transition: "background .5s, box-shadow .5s, color .5s",
                      display: "flex", alignItems: "center", gap: 14,
                    }}>
                    <Stone rgb={active ? GREEN : "238,240,246"} size={10} />
                    <span style={{ flex: 1 }}>{lang === "ru" ? o.ru : o.en}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        <div style={{ marginTop: 32, display: "flex", alignItems: "center", gap: 22, flexWrap: "wrap" }}>
          <NextCapsule
            active={allAnswered && !submitting}
            disabled={!allAnswered || submitting}
            onClick={() => allAnswered && (isLast ? submitAll() : goto(step + 1))}
            testId={isLast ? "upgrade-submit" : "upgrade-continue"}>
            {submitting ? t.analysing : (isLast ? t.submit : t.next)}
          </NextCapsule>
          <span style={{ fontSize: 10.5, letterSpacing: "0.16em", color: "var(--fg-28)" }}>
            {allAnswered ? (isLast ? t.answered_hint : t.next_hint_ready) : t.submit_hint}
          </span>
        </div>
      </div>,
    );
  }

  return wrapper(<div style={{ color: "var(--fg-4)" }}>{t.analysing}</div>);
}

function MetaRow({ label, value, rgb, last }) {
  return (
    <div style={{
      padding: "14px 0",
      borderBottom: last ? "none" : "1px solid var(--fg-06)",
    }}>
      <div style={{ color: `rgba(${rgb},0.7)`, fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 14, color: "var(--fg-86)" }}>{value}</div>
    </div>
  );
}

function hexToRgb(hex) {
  const h = String(hex || "#B06BFF").replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `${r},${g},${b}`;
}
